import { LOGIN_PATH } from "@/utils/global-constants";
import { SATError, SATErrorType } from "@sadtx/sat-error";
import { translate } from "@/utils/i18n";

export const SESSION_AUTH_ERROR_EVENT = "sat014-session-auth-error";

export type SessionAuthErrorEventDetail = {
  errorType: SATErrorType | string;
  message: string;
  title: string;
  description: string[];
  redirectPath: string;
};

const SESSION_REDIRECT_ERROR_TYPES = new Set<string>([
  SATErrorType.TokenEmpty,
  SATErrorType.WrongToken,
  SATErrorType.ExpiredToken,
  SATErrorType.ExpiredRefreshToken,
  SATErrorType.ExpiredAuthCodeToken,
  SATErrorType.DuplicatedLogin,
  SATErrorType.Unauthorized,
  SATErrorType.FailToGetSecretKey,
]);

export function isSessionAuthError(error: Pick<SATError, "type"> | null | undefined): boolean {
  return Boolean(error?.type && SESSION_REDIRECT_ERROR_TYPES.has(error.type));
}

export function dispatchSessionAuthError(error: Pick<SATError, "type" | "message">): void {
  if (typeof window === "undefined" || !isSessionAuthError(error)) return;

  window.dispatchEvent(
    new CustomEvent<SessionAuthErrorEventDetail>(SESSION_AUTH_ERROR_EVENT, {
      detail: {
        errorType: error.type,
        message: error.message,
        ...sessionAuthErrorCopy(error),
        redirectPath: LOGIN_PATH,
      },
    }),
  );
}

function sessionAuthErrorCopy(error: Pick<SATError, "type" | "message">): Pick<
  SessionAuthErrorEventDetail,
  "title" | "description"
> {
  if (error.type === SATErrorType.DuplicatedLogin) {
    return {
      title: translate("session.duplicateLogin.title"),
      description: [
        translate("session.duplicateLogin.description"),
        translate("session.reloginGuide"),
      ],
    };
  }

  return {
    title: translate("session.expired.title"),
    description: [
      error.message || translate("session.expired.description"),
      translate("session.reloginGuide"),
    ],
  };
}
